import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Input } from '../components/ui/input';
import { ArrowLeft, Envelope } from '@phosphor-icons/react';

const API = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [resetToken, setResetToken] = useState(null);

  async function handleSubmit(e) {
    e.preventDefault();
    setError(''); setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/forgot-password`, { email });
      setSubmitted(true);
      if (res.data.reset_token) setResetToken(res.data.reset_token);
    } catch (err) {
      setError(err.response?.data?.detail || 'Something went wrong');
    } finally { setLoading(false); }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100 dark:from-[#25293C] dark:to-[#2F3349] p-4">
      <div className="w-full max-w-md bg-white dark:bg-[#2F3349] rounded-xl shadow-xl p-8 border border-gray-100 dark:border-[#434968]" data-testid="forgot-password-page">
        <Link to="/login" className="flex items-center gap-1 text-sm text-primary hover:underline mb-6" data-testid="back-to-login">
          <ArrowLeft size={16} /> Back to Login
        </Link>

        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
            <Envelope size={22} className="text-primary" weight="duotone" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Forgot Password</h1>
        </div>
        <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">Enter your email and we'll send you a reset link.</p>

        {submitted ? (
          <div className="space-y-4">
            <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
              <p className="text-sm text-green-700 dark:text-green-300 font-medium">Check your email for a password reset link.</p>
              <p className="text-xs text-green-600 dark:text-green-400 mt-1">If you don't see it, check your spam folder.</p>
            </div>
            {resetToken && (
              <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                <p className="text-xs text-blue-600 dark:text-blue-400 font-medium mb-1">Development Mode - Reset Link:</p>
                <Link to={`/reset-password?token=${resetToken}`} className="text-xs text-primary hover:underline break-all" data-testid="dev-reset-link">
                  /reset-password?token={resetToken}
                </Link>
              </div>
            )}
            <Link to="/login" className="block text-center text-sm text-primary hover:underline">Return to Login</Link>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            {error && <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3 text-sm text-red-600 dark:text-red-400">{error}</div>}
            <div>
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 block">Email</label>
              <Input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="you@example.com" required data-testid="forgot-email-input" />
            </div>
            <button type="submit" disabled={loading} className="w-full h-10 bg-primary text-white rounded-lg font-semibold text-sm hover:bg-primary/90 transition-colors disabled:opacity-50" data-testid="forgot-submit-btn">
              {loading ? 'Sending...' : 'Send Reset Link'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
